import { App, message as staticMessage } from "antd";
import type { MessageInstance } from "antd/es/message/interface";
import { useEffect } from "react";

/**
 * antd's static `message.*` cannot read the ConfigProvider theme/locale and logs a console
 * warning for it. The supported way is the instance from `App.useApp()`, but our callers
 * are mostly stores and services outside React. `AppMessageBridge` (mounted once inside
 * <App>) captures that instance, and `message` below forwards to it.
 *
 * Until the bridge has mounted (or if it never does, e.g. in unit tests) calls fall back
 * to the static API, so nothing is ever dropped.
 */
let instance: MessageInstance | null = null;

/** antd's default is 3s, which feels long for a confirmation. */
const DEFAULT_DURATION = 2;

const TYPES = ["success", "error", "warning", "info", "loading"] as const;

/**
 * Tags every toast with `app-toast app-toast-<type>` so toast.css can target it by a class
 * we control instead of antd's internal markup, which changes between versions.
 */
const withTypeClass = (source: MessageInstance, type: (typeof TYPES)[number]) =>
  ((content: unknown, duration?: number, onClose?: () => void) => {
    const className = `app-toast app-toast-${type}`;
    const config =
      content !== null && typeof content === "object" && "content" in content
        ? { duration: DEFAULT_DURATION, ...(content as object) }
        : { content, duration: duration ?? DEFAULT_DURATION, onClose };
    return source.open({ ...config, type, className } as Parameters<
      MessageInstance["open"]
    >[0]);
  }) as MessageInstance["success"];

export const message = new Proxy({} as MessageInstance, {
  get: (_target, prop: keyof MessageInstance) => {
    const source = (instance ?? staticMessage) as MessageInstance;
    if ((TYPES as readonly string[]).includes(prop as string)) {
      return withTypeClass(source, prop as (typeof TYPES)[number]);
    }
    const value = source[prop];
    return typeof value === "function" ? value.bind(source) : value;
  },
});

export const AppMessageBridge: React.FC = () => {
  const { message: appMessage } = App.useApp();

  useEffect(() => {
    instance = appMessage;
    return () => {
      if (instance === appMessage) instance = null;
    };
  }, [appMessage]);

  return null;
};
